// /charges/new — cria cobrança avulsa: pagamento único (Story 4.4) OU recorrente
// sem plano (Story 4.13). O toggle alterna o modo; o submit despacha pro endpoint
// certo (POST /api/charges vs POST /api/subscriptions/standalone).

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  addDaysLocal,
  createManualChargeSchema,
  createStandaloneSubscriptionSchema,
  todayLocal,
  type ManualChargeType,
  type RecurringBillingCycle,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { ClientPicker } from '../../components/clients/ClientPicker';
import { Field, Input, Select, Textarea } from '../../components/clients/form-primitives';
import { NewClientDialog } from '../../components/clients/NewClientDialog';
import { PillButton } from '../../components/clients/PillButton';
import { useCreateManualCharge } from '../../hooks/use-charges';
import { useCreateStandaloneSubscription } from '../../hooks/use-subscriptions';
import type { ClientEntity } from '../../lib/clients-api';
import { extractErrorMessage } from '../../lib/errors';
import { firstErrorMessage } from '../../lib/form-errors';

type ChargeMode = 'single' | 'recurring';

const MODE_OPTIONS: { value: ChargeMode; label: string }[] = [
  { value: 'single', label: 'Pagamento único' },
  { value: 'recurring', label: 'Recorrente' },
];

const TYPE_OPTIONS: { value: ManualChargeType; label: string }[] = [
  { value: 'implantacao', label: 'Implantação' },
  { value: 'consultoria_extra', label: 'Consultoria extra' },
  { value: 'treinamento', label: 'Treinamento' },
  { value: 'desenvolvimento_personalizado', label: 'Dev. personalizado' },
  { value: 'taxa_setup', label: 'Taxa de setup' },
  { value: 'acordo_quitacao', label: 'Acordo de quitação' },
  { value: 'outro', label: 'Outro' },
];

const CYCLE_OPTIONS: { value: RecurringBillingCycle; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
];

const DEFAULT_DUE_DATE_OFFSET_DAYS = 7;

/**
 * Schema único do formulário com campo `mode`. Reusa os `.shape` dos schemas do
 * shared (zero duplicação de regra): campos comuns + `type` (single) e
 * `billingCycle` (recurring), ambos sempre com default válido. O `date` reusa a
 * regra de `dueDate` (regex + >= hoje), idêntica à de `startDate`.
 */
const chargeFormSchema = z.object({
  mode: z.enum(['single', 'recurring']),
  clientId: createManualChargeSchema.shape.clientId,
  amount: createManualChargeSchema.shape.amount,
  description: createManualChargeSchema.shape.description,
  date: createManualChargeSchema.shape.dueDate,
  type: createManualChargeSchema.shape.type,
  billingCycle: createStandaloneSubscriptionSchema.shape.billingCycle,
});
type ChargeFormValues = z.infer<typeof chargeFormSchema>;

export function NewChargePage(): JSX.Element {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<ClientEntity | null>(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const createMutation = useCreateManualCharge();
  const createStandaloneMutation = useCreateStandaloneSubscription();

  const defaultDate = addDaysLocal(todayLocal(), DEFAULT_DUE_DATE_OFFSET_DAYS);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ChargeFormValues>({
    resolver: zodResolver(chargeFormSchema),
    defaultValues: {
      mode: 'single',
      clientId: '',
      amount: undefined as unknown as number,
      description: '',
      date: defaultDate,
      type: 'consultoria_extra',
      billingCycle: 'mensal',
    },
  });

  const mode = watch('mode');
  const isRecurring = mode === 'recurring';
  const isPending = createMutation.isPending || createStandaloneMutation.isPending;

  function onClientChange(client: ClientEntity | null): void {
    setSelectedClient(client);
    setValue('clientId', client?.id ?? '', { shouldValidate: true });
  }

  async function onSubmit(values: ChargeFormValues): Promise<void> {
    try {
      if (values.mode === 'recurring') {
        await createStandaloneMutation.mutateAsync({
          clientId: values.clientId,
          amount: values.amount,
          description: values.description,
          billingCycle: values.billingCycle,
          startDate: values.date,
        });
        toast.success('Cobrança recorrente criada', {
          description:
            'Copie o link de pagamento na lista e envie ao cliente. As próximas saem por ciclo.',
        });
        navigate('/charges?scope=recorrentes');
        return;
      }

      await createMutation.mutateAsync({
        clientId: values.clientId,
        type: values.type,
        amount: values.amount,
        description: values.description,
        dueDate: values.date,
      });
      toast.success('Cobrança criada', {
        description: 'Copie o link de pagamento na lista e envie ao cliente.',
      });
      navigate('/charges?scope=avulsas');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao criar cobrança'));
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-8">
      <button
        type="button"
        onClick={() => navigate('/charges')}
        className="mb-4 flex items-center gap-1.5 text-[12px] text-muted hover:text-ink"
      >
        <ArrowLeft size={14} strokeWidth={1.8} />
        Voltar pra cobranças
      </button>

      <header className="mb-6">
        <h1 className="text-[20px] font-semibold tracking-[-0.005em]">Nova cobrança avulsa</h1>
        <p className="mt-1 text-[13px] text-muted">
          {isRecurring
            ? 'Cobrança recorrente de valor livre, sem vincular a um plano. A 1ª sai agora; as próximas, a cada ciclo.'
            : 'Cobrança única (não-recorrente) pra serviços extras, implantação, treinamento etc.'}
        </p>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit, (errs) =>
          toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
        )}
        className="flex flex-col gap-5"
      >
        <div className="flex flex-col gap-[6px]">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
            Tipo de cobrança
          </span>
          <div
            role="group"
            aria-label="Tipo de cobrança"
            className="inline-flex w-full rounded-[10px] border border-line bg-panel p-1"
          >
            {MODE_OPTIONS.map((opt) => {
              const active = mode === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setValue('mode', opt.value)}
                  className={cn(
                    'flex-1 rounded-[8px] px-4 py-2 text-[13px] font-medium transition-colors',
                    active ? 'bg-panel-3 text-ink' : 'text-muted hover:bg-panel-2 hover:text-ink',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Cliente" required error={errors.clientId?.message}>
          {() => (
            <ClientPicker
              value={selectedClient}
              onChange={onClientChange}
              onCreateNew={() => setShowNewClient(true)}
            />
          )}
        </Field>

        {!isRecurring && (
          <Field label="Tipo" required error={errors.type?.message}>
            {({ inputId }) => (
              <Select id={inputId} hasError={Boolean(errors.type)} {...register('type')}>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field
          label="Valor (R$)"
          required
          error={errors.amount?.message}
          hint="Use ponto como separador decimal. Ex: 150.50"
        >
          {({ inputId }) => (
            <Input
              id={inputId}
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              tabular
              hasError={Boolean(errors.amount)}
              {...register('amount', { valueAsNumber: true })}
            />
          )}
        </Field>

        <Field
          label="Descrição"
          required
          error={errors.description?.message}
          hint="Aparece na fatura do cliente (1-200 caracteres)"
        >
          {({ inputId }) => (
            <Textarea
              id={inputId}
              rows={3}
              maxLength={200}
              hasError={Boolean(errors.description)}
              {...register('description')}
            />
          )}
        </Field>

        {isRecurring && (
          <Field
            label="Ciclo"
            required
            error={errors.billingCycle?.message}
            hint="De quanto em quanto tempo a cobrança se repete."
          >
            {({ inputId }) => (
              <Select
                id={inputId}
                hasError={Boolean(errors.billingCycle)}
                {...register('billingCycle')}
              >
                {CYCLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field
          label={isRecurring ? 'Início' : 'Vencimento'}
          required
          error={errors.date?.message}
          hint={
            isRecurring
              ? 'Data da 1ª cobrança. As próximas seguem o ciclo.'
              : `Padrão hoje + ${DEFAULT_DUE_DATE_OFFSET_DAYS} dias. Editável livremente.`
          }
        >
          {({ inputId }) => (
            <Input
              id={inputId}
              type="date"
              min={todayLocal()}
              hasError={Boolean(errors.date)}
              {...register('date')}
            />
          )}
        </Field>

        <div className="mt-2 flex items-center justify-end gap-2">
          <PillButton type="button" onClick={() => navigate('/charges')}>
            Cancelar
          </PillButton>
          <PillButton type="submit" solid disabled={isPending || !selectedClient}>
            {isPending ? 'Criando…' : isRecurring ? 'Criar recorrente' : 'Criar cobrança'}
          </PillButton>
        </div>
      </form>

      <NewClientDialog
        open={showNewClient}
        onOpenChange={setShowNewClient}
        onCreated={(client) => {
          onClientChange(client);
          setShowNewClient(false);
        }}
      />
    </div>
  );
}
